import { graphql } from 'app/generated/gql'
import { useSearchParams, useParams } from 'react-router-dom'
import { useCallback, useState } from 'react'
import { notifySuccess, notifyError } from 'app/services/notify.client'
import { gqlMutation } from 'app/lib/graphqlClient'

import { MODEL_FIELDS } from 'app/generated/modelFields'

const UPDATE_LOCATION_FACEBOOK = graphql(`
  mutation UpdateLocationFacebook($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      pms
      pmsIntegratedDate
      migrationDate
      sikkaOfficeId
      sikkaPracticeId
      kollaConnectorId
      kollaConsumerId
      kollaPracticeId
      kollaPageSize
      availabilityMode
      callRailCompanyId
      elevenLabsAgentId
      callRailBackfillStartDate
      jotformFormIds
      facebookFormIds
      carestackId
      ringCentralPhoneNumbers
      twilioNumbers {
        id
        type
        phone
        sourceName
        label
        poolId
        areaCode
        poolSize
        sessionDuration
      }
      primaryNumberId
      officeTransferNumberId
      googleAdsCustomerId
      facebookAdSetIds
      city
      state
      address
      zip
      phone
      description
      addressLine2
      timezone
      openHours {
        sunday {
          hours {
            from
            to
          }
          open
        }
        monday {
          hours {
            from
            to
          }
          open
        }
        tuesday {
          hours {
            from
            to
          }
          open
        }
        wednesday {
          hours {
            from
            to
          }
          open
        }
        thursday {
          hours {
            from
            to
          }
          open
        }
        friday {
          hours {
            from
            to
          }
          open
        }
        saturday {
          hours {
            from
            to
          }
          open
        }
      }
      tags
      orgTags
      labels
      products
      type
      callRailRouting {
        tag
        locationId
      }
      primaryTrackingNumber
      primarySmsNumber
      businessPhone
      officeMainNumber
      officeTransferNumber
      jotFormIgnoredForms
      lastPmsSyncDate
      lastSyncFailureDate
      lastPmsSyncStartDate
      lastPmsSyncCompleteDate
      currentStatus
      notes {
        text
        date
        userId
      }
      lastRingCentralSyncDate
      lastCallrailWebhookDate
      lastTwilioWebhookDate
      lastAICallDate
      lastSikkaWebhookDate
      lastJotformWebhookDate
      lastFacebookWebhookDate
      facebookSpendUpdateDate
      googleSpendUpdateDate
      facebookAdsActive
      googleAdsActive
      isSchedulerLive
      isAIAgentLive
      allowDoubleBookingScheduling
      gravityFormsKey
      webflowFormsKey
      googleAdsV2 {
        accountId
        campaignId
        adGroupId
      }
      googleAdsV3 {
        accountId
        campaignIds
        conversionActions
      }
      facebookAdsV2 {
        accountId
        campaignId
        adSetId
      }
      facebookLeadsPages {
        id
        formIds
      }
      jotform {
        folderId
        formIds
      }
      jotformV2 {
        labelId
        formIds
      }
      settings {
        newPatientCalculation
        appointmentsSetNewPatient
        appointmentCompletionStatuses
        transactionBrokenCodes
        newPatientCodes
        ignorePII
        excludeParentsFromNewPatients
        firstVisitSetNewPatient
      }
      archived
      lastGravityFormWebhookDate
      lastPatientSyncDate
      lastAppointmentSyncDate
      lastTransactionSyncDate
      lastProcedureSyncDate
      lastTreatmentPlanSyncDate
      lastPatientReceivedDate
      lastAppointmentReceivedDate
      lastTransactionReceivedDate
      lastProcedureReceivedDate
      lastTreatmentPlanReceivedDate
      ascendLocationId
      alarmQuietStart
      alarmQuietEnd
      dailySyncHour
      pmsIntegrated
    }
  }
`)

export type UseFacebookAdsSettingsReturn = {
  startOpen: boolean
  isFetcherRunning: boolean
  onFacebookChange: (adSetIds: string[]) => void
}

export function useFacebookAdsSettings(): UseFacebookAdsSettingsReturn {
  const [searchParams] = useSearchParams()
  const { locationId } = useParams<{ locationId: string }>()
  const open = searchParams.get(`open`)
  const [isFetcherRunning, setIsFetcherRunning] = useState(false)

  const onFacebookChange = useCallback(
    (adSetIds: string[]) => {
      if (!locationId) return
      setIsFetcherRunning(true)
      gqlMutation(UPDATE_LOCATION_FACEBOOK, {
        id: locationId,
        input: { facebookAdSetIds: adSetIds },
      })
        .then(({ data, error }) => {
          if (data?.updateLocation) {
            notifySuccess(`Facebook Ads Saved`)
          } else {
            notifyError(`Failed to save Facebook Ads: ${error?.message || 'Unknown error'}`)
          }
        })
        .finally(() => setIsFetcherRunning(false))
    },
    [locationId],
  )

  return {
    startOpen: open === `facebook`,
    isFetcherRunning,
    onFacebookChange,
  }
}
